import React, { useState, useEffect, useRef } from 'react';
import './Menu.css';
import menuFront from '../assets/Menu-Front.png';
import menuBack from '../assets/Menu-Back.png';

const Menu = () => {
  const [activeImage, setActiveImage] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomTransform, setZoomTransform] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);

  // Close modal on Escape key & disable background page scroll while open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        switchPage();
      }
    };

    if (activeImage) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeImage]);

  const closeModal = () => {
    setIsZoomed(false);
    setZoomTransform({ x: 0, y: 0 });
    setActiveImage(null);
  };

  const switchPage = () => {
    setIsZoomed(false);
    setZoomTransform({ x: 0, y: 0 });
    setActiveImage((curr) => (curr === menuFront ? menuBack : menuFront));
  };

  // Hybrid handler: in-page modal for desktop (>768px), native zoom viewer for mobile (<=768px)
  const handleCardClick = (e, imageSrc) => {
    if (window.innerWidth > 768) {
      e.preventDefault();
      setIsZoomed(false);
      setZoomTransform({ x: 0, y: 0 });
      setActiveImage(imageSrc);
    }
  };

  // Precise click-to-zoom: accelerated edge curve & safety margin so edge text is never clipped
  const handleImageZoomClick = (e) => {
    e.stopPropagation();
    if (isZoomed) {
      setIsZoomed(false);
      setZoomTransform({ x: 0, y: 0 });
    } else {
      if (!imgRef.current) return;
      const rect = imgRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Exact percentage coordinates within the actual image bounds (0 to 1)
      const u = Math.max(0, Math.min(1, clickX / rect.width));
      const v = Math.max(0, Math.min(1, clickY / rect.height));

      // Zoom scale factor
      const scale = 2.05;

      // Normalized distance from center (-1 to +1)
      const normX = (u - 0.5) * 2;
      const normY = (v - 0.5) * 2;

      // Accelerated curve: clicking outer sections pushes the view all the way to the edge
      const acceleratedX = Math.max(-1, Math.min(1, Math.sign(normX) * Math.pow(Math.abs(normX), 0.55) * 1.35));
      const acceleratedY = Math.max(-1, Math.min(1, Math.sign(normY) * Math.pow(Math.abs(normY), 0.55) * 1.35));

      // Maximum translation bounds
      const maxTx = ((scale - 1) * rect.width) / 2;
      const maxTy = ((scale - 1) * rect.height) / 2;

      // 25px safety padding so edge text (like "Dragon Roll") has comfortable breathing room and is never clipped
      const pad = 25;
      const boundTx = Math.max(0, maxTx - pad);
      const boundTy = Math.max(0, maxTy - pad);

      // Translation offset (locked in place, no moving around)
      const tx = -acceleratedX * boundTx;
      const ty = -acceleratedY * boundTy;

      setZoomTransform({ x: tx, y: ty });
      setIsZoomed(true);
    }
  };

  return (
    <section id="menu" className="menu-section">
      <div className="container menu-container">
        <h2 className="section-title">Our Menu</h2>
        <p className="menu-subtitle-hint">Click either page to open and view the high-resolution menu.</p>

        <div className="menu-grid">
          <a 
            href={menuFront}
            onClick={(e) => handleCardClick(e, menuFront)}
            target="_blank" 
            rel="noopener noreferrer" 
            className="menu-card"
            title="Pyro Pot Menu - Page 1"
          >
            <div className="menu-image-wrapper">
              <img src={menuFront} alt="Pyro Pot All You Can Eat Menu - Page 1" className="menu-image" />
              <div className="menu-zoom-hint">
                <span className="hint-desktop">Click to enlarge</span>
                <span className="hint-mobile">Tap to zoom full-res</span>
              </div>
            </div>
          </a>

          <a 
            href={menuBack}
            onClick={(e) => handleCardClick(e, menuBack)}
            target="_blank" 
            rel="noopener noreferrer" 
            className="menu-card"
            title="Pyro Pot Menu - Page 2"
          >
            <div className="menu-image-wrapper">
              <img src={menuBack} alt="Pyro Pot All You Can Eat Menu - Page 2" className="menu-image" />
              <div className="menu-zoom-hint">
                <span className="hint-desktop">Click to enlarge</span>
                <span className="hint-mobile">Tap to zoom full-res</span>
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* In-Page Lightbox Modal (For Desktop / Larger Screens) */}
      {activeImage && (
        <div 
          className="menu-lightbox-overlay" 
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          <button 
            className="lightbox-close-btn" 
            onClick={closeModal}
            aria-label="Close enlarged menu"
            title="Close (Esc)"
          >
            &times;
          </button>

          <button 
            className="lightbox-nav-btn prev-btn" 
            onClick={(e) => {
              e.stopPropagation();
              switchPage();
            }}
            aria-label="Switch to other page"
            title="Switch page"
          >
            &#10094;
          </button>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <div 
              className={`lightbox-image-box ${isZoomed ? 'zoomed' : ''}`}
              onClick={handleImageZoomClick}
              title={isZoomed ? "Click to reset view" : "Click to zoom into this section"}
            >
              <img 
                ref={imgRef}
                src={activeImage} 
                alt={activeImage === menuFront ? "Pyro Pot Menu - Page 1" : "Pyro Pot Menu - Page 2"} 
                className="lightbox-image" 
                style={{
                  transformOrigin: 'center center',
                  transform: isZoomed 
                    ? `translate(${zoomTransform.x}px, ${zoomTransform.y}px) scale(2.1)` 
                    : 'translate(0px, 0px) scale(1)',
                }}
              />
              <div className="zoom-toggle-badge">
                {isZoomed ? "🔍 Click to Zoom Out" : "🔍 Click Section to Zoom In"}
              </div>
            </div>

            <div className="lightbox-footer">
              <span className="lightbox-page-indicator">
                {activeImage === menuFront ? "Page 1: Soup Bases & Meats" : "Page 2: Small Dishes, Sushi & Drinks"}
              </span>
              <span className="lightbox-hint-text">
                {isZoomed ? "Click anywhere on the image to reset view" : "Click any section to zoom in • Click outside to close"}
              </span>
            </div>
          </div>

          <button 
            className="lightbox-nav-btn next-btn" 
            onClick={(e) => {
              e.stopPropagation();
              switchPage();
            }}
            aria-label="Switch to other page"
            title="Switch page"
          >
            &#10095;
          </button>
        </div>
      )}
    </section>
  );
};

export default Menu;
